import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, LogOut, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSettingsDialog } from '@/contexts/SettingsDialogContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

function initialsFromUser(fullName?: string, username?: string, email?: string): string {
  const source = (fullName || username || email || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

interface ShellAccountMenuProps {
  /** Avatar-only trigger for the tab bar / compact chrome */
  compact?: boolean;
}

export function ShellAccountMenu({ compact = false }: ShellAccountMenuProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openSettings } = useSettingsDialog();

  if (!user) return null;

  const displayName = user.full_name || user.username || user.email;
  const initials = initialsFromUser(user.full_name, user.username, user.email);

  return (
    <div className={cn('flex-shrink-0', compact ? '' : 'w-full')}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <button
              type="button"
              title={displayName}
              aria-label={displayName}
              className="flex h-11 w-11 items-center justify-center rounded-full"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--rcm-color-primary)] text-xs font-semibold text-white">
                {initials}
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-[var(--rcm-color-selected)]"
            >
              <Avatar className="h-9 w-9 flex-shrink-0">
                <AvatarFallback className="bg-[var(--rcm-color-primary)] text-xs font-semibold text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{displayName}</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden />
            </button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent side={compact ? 'bottom' : 'top'} align={compact ? 'end' : 'start'} className="w-52">
          <DropdownMenuItem onClick={() => openSettings()}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
